/**
 * Task Operations Page
 * Loads only the data for the currently open tab. Lazy-loads other tab data when user switches tabs.
 * Uses TAB_API_MANIFEST (dashboardTabManifest.ts) — only that tab's endpoints are called on tab open.
 * Local dev: set REACT_APP_TASKS_SERVICE_BASE_URL=https://localhost:59239 in .env.local or config.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { selectUser } from 'oidc/user.selectors';
import AdminDashboard from './AdminDashboard';
import {
  loadTabDataByManifest,
  fetchAdminDashboardTasksData,
  fetchAdminDashboardCueNotesData,
  fetchAdminDashboardCommentsData,
  fetchAdminDashboardReportsData,
  DEFAULT_OVERVIEW,
} from 'app/services/TaskServices/ManagementDashboardService';
import type { AdminDashboardData } from './AdminDashboard';
import type {
  DashboardTabId,
  DashboardExternalTasksParams,
  DashboardCueNotesParams,
  DashboardCommentsParams,
  OverviewEndpointSource,
  ReportsEndpointSource,
} from 'app/services/TaskServices/ManagementDashboardService';
import { durationToDateRange, DEFAULT_DURATION, type DurationValue } from './durationUtils';
import { Box, CircularProgress, Typography, Alert, Button } from '@mui/material';

/** Must match AdminDashboard defaultTab. Only this tab's data is loaded on initial page visit. */
const DEFAULT_TAB: DashboardTabId = 'overview';

export const TaskOperationsPage: React.FC = () => {
  const userInfo = useSelector(selectUser)?.userInfo;
  const [data, setData] = useState<AdminDashboardData | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [loadingTab, setLoadingTab] = useState<DashboardTabId | null>(null);
  const [loadingGrid, setLoadingGrid] = useState<DashboardTabId | null>(null);
  const [overviewResolvedSources, setOverviewResolvedSources] = useState<Set<string>>(new Set());
  const [reportsResolvedSources, setReportsResolvedSources] = useState<Set<string>>(new Set());
  const [error, setError] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [duration, setDuration] = useState<DurationValue>(DEFAULT_DURATION);
  const durationRef = useRef<DurationValue>(duration);
  durationRef.current = duration;
  const [currentTab, setCurrentTab] = useState<DashboardTabId>(DEFAULT_TAB);
  const currentTabRef = useRef<DashboardTabId>(currentTab);
  currentTabRef.current = currentTab;
  const loadingTabsRef = useRef<Set<DashboardTabId>>(new Set());
  const loadTabRef = useRef<(tab: DashboardTabId, opts?: { setLoading?: boolean; dateRangeOverride?: { startDate: string; endDate: string }; skipRecentActivity?: boolean }) => Promise<void>>(() => Promise.resolve());

  /** Loads tab data via manifest. Same method for tab open and duration change. Uses durationRef so callback stays stable.
   * For Overview/Activity tabs: passes onPartial/onFirstPartial so widgets render as each endpoint resolves (incremental loading). */
  const loadTab = useCallback(
    async (tab: DashboardTabId, loadOptions?: { setLoading?: boolean; dateRangeOverride?: { startDate: string; endDate: string }; skipRecentActivity?: boolean }) => {
      if (loadOptions?.setLoading) {
        setLoading(true);
        setError(false);
      }
      if (!loadOptions?.setLoading && loadingTabsRef.current.has(tab)) return;
      if (!loadOptions?.setLoading) loadingTabsRef.current.add(tab);
      setLoadingTab(tab);
      const dateRange = loadOptions?.dateRangeOverride ?? durationToDateRange(durationRef.current);
      const isOverviewTab = tab === 'overview' || tab === 'activity';
      const isReportsTab = tab === 'reports';
      if (isOverviewTab) setOverviewResolvedSources(new Set());
      if (isReportsTab) setReportsResolvedSources(new Set());
      const manifestOptions = {
        skipRecentActivity: loadOptions?.skipRecentActivity,
        ...(tab === 'sms' && {
          userId: userInfo?.employeeId ?? 0,
          userInfo: userInfo ? { employeeId: userInfo.employeeId, firstName: userInfo.firstName, lastName: userInfo.lastName } : undefined,
        }),
        ...(isOverviewTab && {
          onPartial: (partial: Partial<AdminDashboardData>, resolvedSource: OverviewEndpointSource) => {
            setOverviewResolvedSources((prev) => new Set(prev).add(resolvedSource));
            setData((prev) => {
              const base = prev ?? ({} as AdminDashboardData);
              const next = { ...base };
              if (partial.overview) {
                next.overview = { ...DEFAULT_OVERVIEW, ...base.overview, ...partial.overview } as AdminDashboardData['overview'];
              }
              if (partial.activityLog) next.activityLog = partial.activityLog;
              if (partial.categoryBreakdown) next.categoryBreakdown = partial.categoryBreakdown;
              return next as AdminDashboardData;
            });
          },
          onFirstPartial: () => {
            setLoadingTab((t) => (t === 'overview' || t === 'activity' ? null : t));
            if (loadOptions?.setLoading) setLoading(false);
          },
        }),
        ...(isReportsTab && {
          onReportsPartial: (partial: Partial<AdminDashboardData>, resolvedSource: ReportsEndpointSource) => {
            setReportsResolvedSources((prev) => new Set(prev).add(resolvedSource));
            setData((prev) => ({ ...(prev ?? ({} as AdminDashboardData)), ...partial } as AdminDashboardData));
          },
          onReportsFirstPartial: () => {
            setLoadingTab((t) => (t === 'reports' ? null : t));
            if (loadOptions?.setLoading) setLoading(false);
          },
        }),
      };
      try {
        const result = await loadTabDataByManifest(tab, { pageNumber: 1, pageSize: 25, ...dateRange }, dateRange, manifestOptions);
        setData((prev) => ({ ...prev, ...result } as AdminDashboardData));
      } catch {
        setError(true);
        if (loadOptions?.setLoading) setData(undefined);
      } finally {
        setLoadingTab(null);
        if (loadOptions?.setLoading) setLoading(false);
        else loadingTabsRef.current.delete(tab);
      }
    },
    [userInfo]
  );

  const loadTabData = useCallback(
    async (
      tab: DashboardTabId,
      fetchParams?:
        | ({ pageNumber?: number; pageSize?: number } & Partial<DashboardExternalTasksParams>)
        | ({ pageNumber?: number; pageSize?: number } & Partial<DashboardCueNotesParams>)
        | ({ pageNumber?: number; pageSize?: number } & Partial<DashboardCommentsParams>),
      options?: { listOnly?: boolean }
    ) => {
      const pageNumber = fetchParams?.pageNumber ?? 1;
      const pageSize = fetchParams?.pageSize ?? 25;
      const listOnly = options?.listOnly ?? false;
      if (!listOnly && loadingTabsRef.current.has(tab)) return;
      if (!listOnly) loadingTabsRef.current.add(tab);
      if (listOnly) setLoadingGrid(tab);
      const dateRange = durationToDateRange(durationRef.current);
      try {
        let partial: Partial<AdminDashboardData> = {};
        switch (tab) {
          case 'external-tasks':
            partial = await fetchAdminDashboardTasksData(pageNumber, pageSize, fetchParams, { listOnly, dateRange });
            break;
          case 'cue-notes':
            partial = await fetchAdminDashboardCueNotesData(pageNumber, pageSize, fetchParams, { listOnly, dateRange });
            break;
          case 'comments':
            partial = await fetchAdminDashboardCommentsData(pageNumber, pageSize, fetchParams, { listOnly, dateRange });
            break;
          case 'reports':
            partial = await fetchAdminDashboardReportsData(dateRange);
            break;
          default:
            return;
        }
        setData((prev) => (prev ? { ...prev, ...partial } : (partial as AdminDashboardData)));
      } finally {
        if (listOnly) setLoadingGrid(null);
        if (!listOnly) loadingTabsRef.current.delete(tab);
      }
    },
    []
  );

  loadTabRef.current = loadTab;

  /** Initial load and refresh only. Must NOT depend on loadTab so duration change does not retrigger. */
  useEffect(() => {
    loadTabRef.current(DEFAULT_TAB, { setLoading: true }).catch(() => setError(true));
  }, [refreshTrigger]);

  /** On tab change: same loadTab method, only that tab's endpoints. */
  const handleTabChange = useCallback(
    (tab: DashboardTabId) => {
      setCurrentTab(tab);
      loadTab(tab);
    },
    [loadTab]
  );

  /** On duration change: SAME loadTab method as tab open, just with new date. Nothing else. */
  const handleDurationChange = useCallback(
    (newDuration: DurationValue, activeTab?: DashboardTabId) => {
      setDuration(newDuration);
      const tab = activeTab ?? currentTab;
      setCurrentTab(tab);
      loadTab(tab, { dateRangeOverride: durationToDateRange(newDuration), skipRecentActivity: true });
    },
    [loadTab, currentTab]
  );

  const handleRefresh = () => setRefreshTrigger((p) => p + 1);

  /** Stable refs: fetch handlers use durationRef so they don't change when duration changes — avoids grid re-fetch on duration change. */
  const handleFetchTasksPage = useCallback(
    (pageNumber: number, pageSize: number, filters?: Partial<DashboardExternalTasksParams>) => {
      const dateRange = durationToDateRange(durationRef.current);
      loadTabData('external-tasks', { pageNumber, pageSize, ...dateRange, ...filters }, { listOnly: true });
    },
    [loadTabData]
  );
  const handleFetchCueNotesPage = useCallback(
    (pageNumber: number, pageSize: number, filters?: Partial<DashboardCueNotesParams>) => {
      const dateRange = durationToDateRange(durationRef.current);
      loadTabData('cue-notes', { pageNumber, pageSize, ...dateRange, ...filters }, { listOnly: true });
    },
    [loadTabData]
  );
  const handleFetchCommentsPage = useCallback(
    (pageNumber: number, pageSize: number, filters?: Partial<DashboardCommentsParams>) => {
      const dateRange = durationToDateRange(durationRef.current);
      loadTabData('comments', { pageNumber, pageSize, ...dateRange, ...filters }, { listOnly: true });
    },
    [loadTabData]
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {error && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexShrink: 0 }}>
          <Alert severity="warning" sx={{ flex: 1 }}>
            Could not load from API.
            <Button size="small" sx={{ ml: 1 }} onClick={handleRefresh}>Retry</Button>
          </Alert>
        </Box>
      )}
      <AdminDashboard
        data={data}
        defaultTab={DEFAULT_TAB}
        key={error ? 'fallback' : 'dynamic'}
        duration={duration}
        loadingTab={loadingTab}
        loadingGrid={loadingGrid}
        overviewResolvedSources={overviewResolvedSources}
        reportsResolvedSources={reportsResolvedSources}
        onDurationChange={handleDurationChange}
        onTabChange={handleTabChange}
        onFetchTasksPage={handleFetchTasksPage}
        onFetchCueNotesPage={handleFetchCueNotesPage}
        onFetchCommentsPage={handleFetchCommentsPage}
      />
    </Box>
  );
};
